import React, { useEffect, useState } from "react";
import { CyberpunkHeroBackground } from "./CyberpunkHeroBackground";
import { sanitizeTokens, safeLookup } from './ui/chart';
import { useTranslation } from 'react-i18next';

export function ThreeDHeroSection() {
  const { t } = useTranslation();
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  // Dynamically get tickers from translation file
  const tickers = t('token_tickers', { returnObjects: true }) as string[];
  // Map to the structure needed for rendering
  const TOKENS = Array.isArray(tickers)
    ? tickers.map(symbol => ({
        symbol,
        name: symbol,
        icon: `/images/coins/svg/${symbol}.svg`
      }))
    : [];
  const sanitizedTokens = sanitizeTokens(TOKENS);

  useEffect(() => {
    let isMounted = true;
    const fetchPrices = async () => {
      try {
        setLoading(true);
        const res = await fetch("/live");
        const data = await res.json();
        if (isMounted) {
          setPrices(data.prices || {});
        }
      } catch (e) {
        // Optionally handle error
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 10000); // Poll every 10s

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  return (
    <section className="relative flex flex-col items-center justify-center min-h-[80vh] py-20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1833 0%, #07102a 60%, #1a223a 100%)' }}>
      <div className="w-full max-w-4xl mx-auto px-4 flex flex-col items-center">
        {/* Animated Cyberpunk Background */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <CyberpunkHeroBackground />
        </div>
        {/* Solana-inspired accent glows */}
        <div className="absolute inset-0 z-0 pointer-events-none" style={{
          background: `radial-gradient(circle at 70% 20%, rgba(20,241,149,0.18) 0%, rgba(10,24,51,0.0) 60%),
                       radial-gradient(circle at 20% 80%, rgba(3,225,255,0.13) 0%, rgba(10,24,51,0.0) 70%),
                       radial-gradient(circle at 90% 90%, rgba(153,69,255,0.10) 0%, rgba(10,24,51,0.0) 80%)`
        }} />
        {/* Remove or reduce black overlay for more navy vibrancy */}
        <div className="absolute inset-0 bg-[#0a1833]/60 backdrop-blur-sm z-0" />
        {/* Mascot with floating animation */}
        <div className="relative mb-8 mt-2.5 flex flex-col items-center" style={{ marginTop: '10px' }}>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-[#14F19533] blur-3xl opacity-60 z-0" />
          <img
            src="/images/branding/SOLANA-VOLUME-BOT-MASCOTTE.avif"
            alt={t('mascot_logo_alt')}
            className="w-40 h-40 md:w-56 md:h-56 rounded-full shadow-2xl relative z-10"
          />
        </div>
        {/* Headline translated */}
        <h1 className="text-4xl md:text-6xl font-extrabold text-center drop-shadow-2xl mb-4 leading-tight bg-clip-text text-transparent bg-gradient-to-r from-[#14F195] to-[#9945FF]" style={{textShadow: '0 4px 32px #0a1833'}}>
          {t('home_headline')}
        </h1>
        {/* Subheadline translated */}
        <p className="max-w-2xl mx-auto text-lg md:text-2xl font-medium text-gray-200 text-center mb-6">
          {t('home_subheadline')}
        </p>
        {/* Security/UX Badge */}
        <div className="flex justify-center mb-8">
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-[#0e2b23] via-[#0a5c4e] to-[#3a1c5c] border border-[#14F195]/40 shadow-lg text-base md:text-lg font-semibold text-white">
            <span className="text-[#14F195]">{t('3dherosection._no_private_key')}</span>
            <span className="text-[#03E1FF]">{t('3dherosection._no_wallet_connection')}</span>
            <span className="text-accent">{t('3dherosection._no_password_required')}</span>
          </span>
        </div>
        {/* CTA Buttons translated */}
        <div className="flex flex-col md:flex-row gap-4 justify-center items-center mb-6">
          <a href="#pricing" className="relative z-10 inline-block px-12 py-5 rounded-full text-xl font-bold text-white bg-gradient-to-r from-[#14F195] via-[#03E1FF] to-[#9945FF] shadow-xl border-2 border-[#14F195]/40 transition-transform duration-200 hover:scale-105 hover:shadow-[0_0_32px_8px_#14F19555,0_0_0_4px_#9945FF33] focus:outline-none focus:ring-4 focus:ring-[#14F195]/40 backdrop-blur-md">
            {t('home.ctaStartFreeTrial')}
            <span className="absolute inset-0 rounded-full pointer-events-none animate-pulse bg-gradient-to-r from-[#14F19533] via-[#03E1FF22] to-[#9945FF33] opacity-40 blur-sm" />
          </a>
          <a href="#demo" className="relative z-10 inline-block px-8 py-5 rounded-full text-lg font-semibold text-white bg-gradient-to-r from-[#0a1833] to-[#23272e] border-2 border-[#03E1FF]/30 shadow hover:scale-105 hover:shadow-xl transition-transform duration-200">
            {t('home.ctaSeeHowItWorks')}
          </a>
        </div>
        {/* Trust Badge translated */}
        <div className="flex justify-center mb-8">
          <span className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-[#0a1833]/80 border border-[#14F195]/30 shadow text-base md:text-lg font-semibold text-[#14F195]">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#14F195" opacity="0.15"/><path d="M7 13l3 3 7-7" stroke="#14F195" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {t('home.trustedBy')}
          </span>
        </div>
        {/* Price Ticker - 100% stable, no animation, no glow */}
        <div className="w-full flex justify-center items-center mt-4 mb-2">
          <div className="flex flex-row gap-3 min-w-max px-2">
            {sanitizedTokens.map(token => (
              <div
                key={token.symbol}
                className="flex flex-col items-center justify-center min-w-[80px] px-3 py-2 rounded-xl border border-[#23272e]/60 bg-[#0a1833]/60"
                style={{ height: 64 }}
              >
                <img
                  src={token.icon}
                  alt={token.symbol}
                  className="w-8 h-8 mb-1"
                  style={{ objectFit: 'contain' }}
                />
                <span className="text-xs text-white font-semibold" style={{ fontSize: '11px', lineHeight: '1.1' }}>{token.symbol}</span>
                <span className="text-[#14F195] font-mono font-bold" style={{ fontSize: '11px', lineHeight: '1.1' }}>
                  {safeLookup(prices, token.symbol, null) !== null && safeLookup(prices, token.symbol, null) !== undefined ? safeLookup(prices, token.symbol, null).toLocaleString(undefined, { maximumFractionDigits: 6 }) : '--'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}